import { Icons } from '@cockpitai/components/atoms'
import { Avatar } from '@cockpitai/components/atoms/Avatar'
import { Typography } from '@cockpitai/components/atoms/Typography'
import { routesPath } from '@cockpitai/constants/routesPath'
import { useChatStore } from '@cockpitai/store/chat'
import { useSession } from '@cockpitai/store/session/useSession'
import { useQueryClient } from '@tanstack/react-query'

import { EOccupationType } from '@cockpitai/constants/user'
import { useLevelNavigation } from '@cockpitai/store/navigation/useLevelNavigation'
import { useSideBarStore } from '@cockpitai/store/navigation/useSideBarStore'
import { goToScrollTop } from '@cockpitai/utils'
import { sendAnalyticsEvent } from '@cockpitai/utils/analytics'
import { isMobile } from '@cockpitai/utils/device'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import { useLocation, useNavigate } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import Authorize, { BUSINESS_CODE } from '../Authorize'
import { useSideBar } from '../Sidebar/SideBarContext'
import { HeaderChatActions } from './components/HeaderChatActions'
import { HeaderItem } from './components/HeaderItem'
import { NavigationLevel } from './components/NavigationLevel'

export function Header() {
  const { pathname } = useLocation()

  const queryClient = useQueryClient()

  const { setSidebarIsOpen } = useSideBar()

  const { resetNavigation, navigationHistory } = useLevelNavigation()

  const { setIsStartedChat, clearMessages } = useChatStore()

  const navigate = useNavigate()

  const { user, setAuth, setUser } = useSession()

  const { resetFavorites } = useSideBarStore()

  const isMajorLevel =
    !!(user?.occupationType && user?.occupationType <= EOccupationType.NETWORK_LEADER) ||
    !user?.occupationType

  const newChat = () => {
    clearMessages()

    setIsStartedChat(false)

    goToScrollTop()

    sendAnalyticsEvent('header_menu_click', { name: 'meu_ai' })
  }

  const handleLogout = () => {
    setAuth(null)

    setUser(null)

    queryClient.clear()

    resetNavigation()

    resetFavorites()

    navigate(routesPath.dashboard.home, { replace: true })

    setSidebarIsOpen(false)
  }

  const handleItemAction = (menuName: string) => {
    resetNavigation()
    goToScrollTop()

    sendAnalyticsEvent('header_menu_click', { name: menuName })
  }

  const hideMktShare = true

  return (
    <header
      className={twMerge(navigationHistory.length > 0 && 'active-breadcrumb')}>
      <div className="mr-3 hidden gap-3 lg:flex xl:gap-6">
        <div className="flex rounded-lg bg-solid">
          <NavigationLevel />

          <HeaderItem
            onClick={newChat}
            path={routesPath.dashboard.chat}
            isActive={pathname?.includes('chat')}>
            <Icons.Stars gradient={pathname?.includes('chat') || undefined} />
            Meu AI
          </HeaderItem>

          <HeaderItem
            onClick={() => handleItemAction('numeros')}
            path={routesPath.dashboard.bigNumbers}
            isActive={pathname?.includes('big-numbers')}>
            Números
          </HeaderItem>

          <HeaderItem
            onClick={() => handleItemAction('visoes')}
            path={routesPath.dashboard.bigViews}
            isActive={pathname?.includes('big-views')}>
            Visões
          </HeaderItem>

          <HeaderItem
            onClick={() => handleItemAction('dispersao')}
            path={routesPath.dashboard.bigQuestions}
            isActive={pathname?.includes('big-questions')}>
            Dispersão
          </HeaderItem>

          {/* Temporary change for presentation! Remove this conditional after */}
          {!hideMktShare && (
            <Authorize
              businessCode={[BUSINESS_CODE.ADMIN, BUSINESS_CODE.VEICULOS]}>
              {isMajorLevel && (
                <>
                  <HeaderItem
                    onClick={() => handleItemAction('market_share')}
                    path={routesPath.dashboard.marketShare}
                    isActive={pathname?.includes('market-share')}>
                    Market Share
                  </HeaderItem>

                  <HeaderItem
                    onClick={() => handleItemAction('conversao')}
                    path={routesPath.dashboard.conversions}
                    isActive={pathname?.includes('conversions')}>
                    Conversão
                  </HeaderItem>
                </>
              )}
            </Authorize>
          )}

          {/* {user?.occupationType && user?.occupationType <= EOccupationType.LEADER && (
            <HeaderItem
              onClick={() => handleItemAction('hora_por_hora')}
              path={routesPath.dashboard.hourByHour}
              isActive={pathname.includes('hour-by-hour')}>
              Hora em Hora
            </HeaderItem>
          )} */}

          {import.meta.env.VITE_CONSULTANT !== 'true' && (
            <HeaderItem
              onClick={() => handleItemAction('fidelity')}
              path={routesPath.dashboard.fidelity}
              isActive={pathname.includes('fidelity')}>
              Fidelidade
            </HeaderItem>
          )}

          {import.meta.env.VITE_CONSULTANT !== 'true' && (
            <HeaderItem
              onClick={() => handleItemAction('propostas')}
              path={routesPath.dashboard.proposals}
              isActive={pathname.includes('proposals')}>
              Propostas D0
            </HeaderItem>
          )}

          <HeaderItem
            onClick={() => handleItemAction('meus_paineis')}
            path={routesPath.dashboard.myPanels}
            isActive={pathname.includes('my-panels')}>
            Meus Painéis
          </HeaderItem>
        </div>

      </div>

      <div className="flex items-center justify-between gap-3 xl:gap-6">
        {/*
        <div className="flex rounded-lg bg-gray-850">
          <Button className="border-transparent bg-transparent px-2.5 py-3 xl:px-3">
            <Icons.Search className="text-gray-400" />
          </Button>
          <Button className="border-transparent bg-transparent px-2.5 py-3 xl:px-3">
            <Icons.Bars className="text-gray-400" />
          </Button>
          <Notifications />
        </div>
        */}

        <HeaderChatActions />

        <DropdownMenu>
          <DropdownMenuTrigger className="border-none outline-none">
            <Avatar
              imageUrl={user?.profile_image_url}
              size="small"
              title={user?.name ?? 'Usuário'}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="mr-2 mt-2 w-44 rounded-md bg-gray-850 p-2">
            <DropdownMenuItem className="outline-none">
              <button
                className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-gray-400 hover:bg-gray-800 hover:text-white"
                onClick={handleLogout}>
                <Icons.Exit className="text-inherit" />
                <Typography>Sair</Typography>
              </button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isMobile() && (
        <div className="custom-scrollbar wrapper-breadcrumb w-full">
          <NavigationLevel />
        </div>
      )}
    </header>
  )
}
